import axios from "axios";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useSetRecoilState } from "recoil";
import { authFormState } from "../../Atoms/auth/AuthFormState";
import { API_URL } from "../../config";
import Loader from "../Loader";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Loader2, EyeOff, Eye } from "lucide-react";
import OTPInput from "react-otp-input";
interface IFormValues {
  email: string;
  otp: number;
  new_password: string;
}

type ResetPasswordProps = {
  toast?: any;
};

const ResetPassword: React.FC<ResetPasswordProps> = (
  props: ResetPasswordProps
) => {
  // router declearation
  const router = useRouter();
  const { t } = useTranslation("common-page");
  // global state manipulation
  const setFormState = useSetRecoilState(authFormState);
  const [showChangePasswordFields, setShowChangePasswordFields] =
    useState(false);
  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState("");
  const [resendTimer, setResendTimer] = useState(0);
  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<IFormValues>();

  // handling the from submit button
  const onSubmit = async (formData: any) => {
    setLoading(true);
    var url = new URL("send_otp_password_reset", API_URL);
    url.searchParams.append("email", formData.email as string);
    const res = await axios.post(url.toString(), {});
    const data = res;
    if (data.status == 200) {
      setShowChangePasswordFields(true);
      setResendTimer(60); // start countdown after first OTP
    }
    props.toast(true, data.data.message);

    setLoading(false);
    //data.message
  };

  const onSubmitOTP = async (formData: any) => {
    setLoading(true);
    try {
      var url = new URL("reset_password", API_URL);
      url.searchParams.append("email", formData.email as string);
      url.searchParams.append("new_password", formData.new_password as string);
      url.searchParams.append("otp", otp);
      const res = await axios.post(url.toString(), {});
      const data = await res;
      if (data.status == 200) {
        setFormState({ view: "login" });
        props.toast(true, data.data.message);
      }
    } catch (e: any) {
      props.toast(false, e.response.data.detail);
      setLoading(false);
    }
    setLoading(false);
    //data.message
  };

  const resendOTP = async () => {
    if (loading == true) {
      return false;
    }
    setLoading(true);
    var url = new URL("send_otp_password_reset", API_URL);
    url.searchParams.append("email", getValues("email") as string);
    const res = await axios.post(url.toString(), {});
    const data = await res;
    setLoading(false);
    props.toast(true, data.data.message);
  };

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [resendTimer]);

  return (
    <>
      <div className="max-w-[500px]">
        {showChangePasswordFields == false && (
          <form
            action=""
            method="post"
            onSubmit={handleSubmit(onSubmit)}
            className="flex flex-col space-y-5"
          >
            <div className="text-center">
              <Input
                {...register("email", {
                  required: true,
                  pattern: {
                    value: /\S+@\S+\.\S+/,
                    message: t("Please enter a valid email address."),
                  },
                })}
                type="email"
                placeholder={t("Enter registered email address")}
                name="email"
                autoComplete="off"
              />
              {errors?.email && (
                <div className="danger">
                  <div className="text-red-foreground text-center mt-3 ">
                    {errors.email?.type === "required" && (
                      <p>{t("E-mail is required.")}</p>
                    )}
                    {errors.email?.type === "pattern" && (
                      <p>{errors.email?.message}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="text-center flex gap-4 justify-center">
              <Button
                type="button"
                variant={"outline"}
                onClick={() => setFormState({ view: "login" })}
              >
                {t("Cancel")}
              </Button>
              <Button type="submit">
                {t("Submit")}
                {loading ? (
                  <Loader2 className="ml-2 h-5 w-5 animate-spin" />
                ) : (
                  ""
                )}
              </Button>
            </div>
          </form>
        )}
        {showChangePasswordFields == true && (
          <form
            action=""
            method="post"
            onSubmit={handleSubmit(onSubmitOTP)}
            className="flex flex-col space-y-4 resetForm"
          >
            <div className="text-left flex justify-between  items-center gap-4 ">
              <OTPInput
                value={otp}
                onChange={setOtp}
                shouldAutoFocus
                numInputs={4}
                renderSeparator={<span className="text-transparent">-</span>}
                renderInput={({
                  style: { color, background, ...style },
                  ...props
                }) => (
                  <Input
                    {...props}
                    style={{
                      ...style,
                      color: void 0,
                      backgroundColor: void 0,
                      width: 40,
                    }}
                    autoComplete="off"
                  />
                )}
              />

              {errors?.otp && (
                <div className="text-red-foreground text-center mt-3">
                  {errors.otp?.type === "required" && (
                    <p>{t("Verification code is required")}</p>
                  )}
                </div>
              )}

              <p className=" mb-2 flex flex-row justify-center text-primary items-center w-40 bg-red- ">
                <span
                  className={`text-sm select-none underline underline-offset-4 ${
                    resendTimer > 0
                      ? "cursor-not-allowed text-gray-400"
                      : "cursor-pointer hover:text-primary text-muted-foreground"
                  }`}
                  onClick={() => {
                    if (resendTimer === 0) resendOTP();
                  }}
                >
                  {resendTimer > 0
                    ? `${t("Resend code")} (${resendTimer}s)`
                    : t("Resend code")}
                </span>
              </p>
            </div>
            <div className="text-left">
              <Input
                {...register("new_password", {
                  required: true,
                  pattern: /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{6,}$/,
                })}
                type="password"
                placeholder={t("Your new password")}
                name="new_password"
                autoComplete="off"
              />
              {errors?.new_password && (
                <div className="danger">
                  <div className="text-red-foreground text-center mt-3 ">
                    {errors.new_password?.type === "required" && (
                      <p>{t("New password is required")}</p>
                    )}
                    {errors.new_password?.type === "pattern" && (
                      <p>
                        {t(
                          "Your password must be at least 6 characters and contain at least one uppercase letter, one lowercase letter, and one digit."
                        )}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="text-center">
              {loading ? (
                <Loader />
              ) : (
                <Button type="submit">{t("Submit")}</Button>
              )}
            </div>
          </form>
        )}
      </div>
      <div className="space-y-4">
        <div className="text-center text-sm mt-5 text-muted-foreground">
          <div>
            <span className="">{t("To login, click")} </span>
            <span
              className="cursor-pointer select-none underline underline-offset-4 hover:text-primary"
              onClick={() => setFormState({ view: "login" })}
            >
              {t("here")}
            </span>
          </div>
          <div>
            <span className="">{t("Click")}</span>{" "}
            <span
              className="cursor-pointer select-none underline underline-offset-4 hover:text-primary"
              onClick={() => router.push("https://mktdynamics.com/")}
            >
              {t("here")}
            </span>{" "}
            <span className="">{t("to go back to the main website")}</span>
          </div>
        </div>
        <hr className="border-primary" />
        <div className="text-center text-sm text-muted-foreground">
          <span>{t("Having trouble logging in? Drop us a message-")}</span>
          <span
            className="cursor-pointer select-none underline underline-offset-4 hover:text-primary"
            onClick={() => {
              window.location.href =
                "mailto:contact@trylevel2.com?subject=Request%20Assistance&body=Hi%20Team,%0DI%20need%20assistance.%20My%20contact%20email%20is%20{enter%20your%20email%20address}%0D%0D%0D%0D";
            }}
          >
            {t("contact@trylevel2.com")}
          </span>
        </div>
      </div>
    </>
  );
};
export default ResetPassword;
